import React, {useEffect, useRef, useState} from 'react';

import {useStore} from 'react-redux';
import ReconnectingWebSocket from 'reconnecting-websocket';

import {chatSubscriptionRegistry} from 'app/subscriptions/subscriptionRegistry';
import {sendSentryMessage} from 'app/utils/sentry';
import {WebsocketContext} from 'app/ws/context';
import {handleUserjobUpdate} from 'app/ws/handlers/jobUpdate';
import {handleModelUpdate} from 'app/ws/handlers/modelUpdate';
import {handleTaskStatus} from 'app/ws/handlers/taskStatus';
import {WebSocketRouter} from 'app/ws/ws-router';

const WEBSOCKET_OPEN_FLAG = 1;

interface Props {
  routers: {[namespace: string]: WebSocketRouter};
  children: React.ReactNode;
}

export function WebsocketProvider({routers, children}: Props) {
  const store = useStore();
  const socket = React.useRef<ReconnectingWebSocket | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const prevIsOpen = useRef(isOpen);

  const sendWebsocketMessage = React.useCallback((payload: string): void => {
    if (socket.current?.readyState === WEBSOCKET_OPEN_FLAG) {
      socket.current!.send(payload);
    }
  }, []);

  const chatUpdate = (event: string, {data}: {data: any}) => {
    return chatSubscriptionRegistry.process(data);
  };

  useEffect(() => {
    setIsClosing(prevIsOpen.current && !isOpen);
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const WEBSOCKET_URL = 'wss://' + window.location.host + '/ws/';

    const router = new WebSocketRouter('root-router', routers);
    router.register_handler('model-update', handleModelUpdate);
    router.register_handler('chat-update', chatUpdate);
    router.register_handler('task-status', handleTaskStatus);
    router.register_handler('userjob-update', handleUserjobUpdate);

    function handleMessage(e: MessageEvent) {
      const content = JSON.parse(e.data);
      // convert old messages to new format
      // TODO: update backend code to use new format so we can remove this
      const message =
        content.action != null || content.payload != null
          ? {
              event: content.action,
              data: content.payload,
            }
          : content;

      const routed = router.route(
        message.event,
        message,
        sendWebsocketMessage,
        store
      );

      if (!routed) {
        sendSentryMessage(
          `WebsocketProvider: No handler for message event type: ${message.event}`,
          {
            extra: {
              data: JSON.stringify(message.data),
            },
          }
        );
      }
    }

    if (window.WebSocket) {
      // eslint-disable-next-line
      // @ts-ignore ReconnectiongWebSocket is fine but the types are wrong?
      socket.current = new ReconnectingWebSocket(WEBSOCKET_URL, [], {
        connectionTimeout: 10000,
        maxReconnectionDelay: 20000,
        minReconnectionDelay: 3000,
      });
      socket.current!.onopen = () => setIsOpen(true);
      socket.current!.onclose = () => setIsOpen(false);
      socket.current!.onerror = () => setIsOpen(false);

      socket.current!.onmessage = handleMessage;

      return () => {
        if (socket.current) {
          // @ts-expect-error I am calling the ``close`` method from
          // ReconnectingWebSocket, not from window.WebSocket.
          socket.current.close(1000, '', {
            keepClosed: true,
            fastClose: true,
            delay: 0,
          });
        }
      };
    }
  }, []);

  return (
    <WebsocketContext.Provider
      value={{
        sendWebsocketMessage,
        isWSOpen: isOpen,
        isWSClosing: isClosing,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
}
